import React, { useEffect, useRef, useState } from "react";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useI18n } from "../../lib/i18n";

/**
 * Premium auto-rotating testimonials carousel.
 * - Snaps to one card at a time on mobile, 3 visible on desktop (CSS scroll-snap)
 * - Auto-advances every 6s, pauses on hover/focus
 * - Prev/Next arrows + clickable dots
 */
export const Testimonials = () => {
  const { t } = useI18n();
  const list = t.testimonials.list;
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const pausedRef = useRef(false);

  // Auto-advance
  useEffect(() => {
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setActive((a) => (a + 1) % list.length);
    }, 6000);
    return () => clearInterval(id);
  }, [list.length]);

  // Scroll snap on change — scroll the carousel TRACK horizontally only.
  // Previously this used card.scrollIntoView() which scrolls the nearest
  // scrollable ancestor — that caused the whole page to jump to the
  // Testimonials section on initial mount (active=0). Using track.scrollTo
  // confines the scroll to the carousel itself.
  const isFirstRunRef = useRef(true);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    if (isFirstRunRef.current) {
      // Position the first slide without animation and without affecting
      // page scroll on initial render.
      isFirstRunRef.current = false;
      const card = track.children[active];
      if (card) {
        track.scrollLeft = card.offsetLeft - (track.clientWidth - card.clientWidth) / 2;
      }
      return;
    }
    const card = track.children[active];
    if (!card) return;
    const targetLeft = card.offsetLeft - (track.clientWidth - card.clientWidth) / 2;
    track.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
  }, [active]);

  return (
    <section
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
    >
      <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-px bg-brand-orange" />
            <span className="label-mono">{t.testimonials.kicker}</span>
          </div>
          <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-[56px] tracking-tight text-white leading-[1.05]">
            {t.testimonials.title}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActive((a) => (a - 1 + list.length) % list.length)}
            data-testid="testimonials-prev"
            aria-label="Previous"
            className="w-11 h-11 rounded-full glass flex items-center justify-center text-white/70 hover:text-white hover:border-brand-orange/40 transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setActive((a) => (a + 1) % list.length)}
            data-testid="testimonials-next"
            aria-label="Next"
            className="w-11 h-11 rounded-full glass flex items-center justify-center text-white/70 hover:text-white hover:border-brand-orange/40 transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="
          flex gap-6 overflow-x-auto pb-6 -mx-6 px-6 md:mx-0 md:px-0
          snap-x snap-mandatory scroll-smooth no-scrollbar
        "
      >
        {list.map((c, i) => (
          <article
            key={i}
            data-testid={`testimonial-${i}`}
            className="
              snap-center shrink-0
              w-[88%] sm:w-[55%] lg:w-[32%]
              glass rounded-3xl p-8 md:p-9
              flex flex-col gap-5
              transition-all duration-300
              hover:-translate-y-1 hover:border-brand-orange/30
            "
          >
            <div className="flex items-center gap-1" aria-label={`Rating ${c.r || 5} of 5`}>
              {Array.from({ length: 5 }).map((_, j) => (
                <Star
                  key={j}
                  className={`w-4 h-4 ${j < (c.r || 5) ? "fill-brand-orange text-brand-orange" : "text-white/20"}`}
                />
              ))}
            </div>
            <Quote className="w-7 h-7 text-brand-azure" />
            <p className="text-white/85 leading-relaxed text-[17px]">&ldquo;{c.q}&rdquo;</p>
            <div className="mt-auto pt-2">
              <div className="font-display font-semibold text-white/90 text-sm">{c.a}</div>
              {c.role && <div className="label-mono text-white/45 mt-1">{c.role}</div>}
            </div>
          </article>
        ))}
      </div>

      {/* Dots */}
      <div className="flex items-center justify-center gap-2 mt-8">
        {list.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            data-testid={`testimonial-dot-${i}`}
            aria-label={`Go to testimonial ${i + 1}`}
            className={`
              h-1.5 rounded-full transition-all duration-300
              ${i === active ? "w-8 bg-brand-orange" : "w-1.5 bg-white/20 hover:bg-white/40"}
            `}
          />
        ))}
      </div>
    </section>
  );
};
